import axios, { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import { APIError } from "@api/types/backendTypes";

export interface FacebookDataset {
  id: number;
  organisation: string;
  dataset_id: string;
  capi_token: string;
  created: string;
  updated: string;
}

export const useGetFacebookDatasets = (organisationId?: string) => {
  const { data } = useAuth();

  return useQuery({
    queryKey: ["datasets", organisationId],
    queryFn: async (): Promise<FacebookDataset[]> => {
      if (!data?.token) {
        throw new Error("User Session not available");
      }
      if (!organisationId) {
        throw new Error("Organisation ID is required");
      }

      try {
        const headers: Record<string, string> = {
          Accept: "application/json",
          "Content-Type": "application/json",
          "tracify-token": data?.token ?? "",
        };

        const response = await axios.get(
          `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/facebook-datasets/${organisationId}/get`,
          {
            headers,
          }
        );

        if (response.status !== 200) {
          throw new Error(response.data.error);
        }

        return response.data;
      } catch (err: any) {
        if (axios.isAxiosError(err)) {
          if (!err.response) {
            throw err;
          }
          const axiosError = err as AxiosError;

          // Handle specific error cases
          if (axiosError.response?.status === 404) {
            throw new Error("Organisation not found");
          } else if (axiosError.response?.status === 422) {
            throw new Error("Invalid organisation ID");
          }

          const apiError = axiosError.response?.data as APIError;
          throw new Error(apiError?.error ?? axiosError.message);
        }
        throw new Error("Unknown error occurred!");
      }
    },
    enabled: !!data?.token && !!organisationId,
  });
};
